import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Col, Row } from 'react-bootstrap'
import { useErrorService } from '@abroad/components'
import { ClearFilters, FilterSelection, MultiRangeSlider } from '../common'
import API from '../../utils/API'
import { getNameArrayFromCompanies } from '../../utils/utility'
import { DateRangePicker } from '../assessments'
import { getCurrentDate } from '../../utils/date'
import CONSTANTS from '../../utils/constants'

const PaymentFilters = ({
  globalFilter,
  setGlobalFilter,
  handleClearFilter,
  isLoading,
}) => {
  const [companies, setCompanies] = useState({
    isLoading: true,
    companies: [],
  })
  const [ecoSystems, setEcoSystems] = useState({
    isLoading: true,
    ecoSystems: [],
  })
  const [promoCodes, setPromoCodes] = useState({
    isLoading: true,
    promoCodes: [],
  })
  const [quests, setQuests] = useState({
    isLoading: true,
    quests: [],
  })
  const [isResetForm, setIsResetForm] = useState(false)
  const picker = useRef()
  const Error = useErrorService()

  useEffect(() => {
    const getCompanyData = async () => {
      try {
        let { data } = await API.AdminAPI.companyList.getCompanyData(
          '?excludeEnterprise=true',
        )
        data = getNameArrayFromCompanies(data)
        if (data) {
          setCompanies({
            isLoading: false,
            companies: data?.map((company) => ({
              key: company,
              value: company,
            })),
          })
        }
      } catch (e) {
        setCompanies({
          isLoading: false,
          companies: [],
        })
        Error.showError(e)
      }
    }
    getCompanyData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const getEcoSystemsData = async () => {
      try {
        const { data } = await API.AdminAPI.teams.getEcoSystemsData(
          `?excludeEnterprise=true`,
        )
        if (data) {
          setEcoSystems({
            isLoading: false,
            ecoSystems: data?.map((ecosystem) => ({
              key: ecosystem,
              value: ecosystem,
            })),
          })
        }
      } catch (e) {
        setEcoSystems({
          isLoading: false,
          ecoSystems: [],
        })
        Error.showError(e)
      }
    }
    getEcoSystemsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const getPromocodeData = async () => {
      try {
        const { data } = await API.AdminAPI.userList.getPromocodeData()
        if (data) {
          const sortedPromo = data?.sort((a, b) =>
            a?.code?.localeCompare(b?.code),
          )
          setPromoCodes({
            isLoading: false,
            promoCodes: sortedPromo?.map((promoCode) => ({
              key: promoCode?.code,
              value: promoCode.code?.toUpperCase(),
            })),
          })
        }
      } catch (e) {
        setPromoCodes({
          isLoading: false,
          promoCodes: [],
        })
        Error.showError(e)
      }
    }
    getPromocodeData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const getQuestData = async () => {
      try {
        const { data } = await API.AdminAPI.quests.getQuestList({
          page: 1,
          limit: 500,
          forCopyFrom: true,
        })
        if (data) {
          // Calculate max characters based on screen width
          // Approximate: 1 character ≈ 0.5rem at 0.875rem font size
          // Account for padding and borders
          const getMaxChars = () => {
            if (window.innerWidth >= 1200) {
              return 50 // Large screens: col-4 ≈ 300px
            } else if (window.innerWidth >= 992) {
              return 40 // Medium screens
            } else if (window.innerWidth >= 768) {
              return 30 // Small screens
            } else {
              return 25 // Extra small screens
            }
          }

          const maxChars = getMaxChars()
          const questsData = data?.map((quest) => {
            const fullName = quest.title || quest.name || `Quest ${quest._id}`
            // Store full name for dropdown options, truncated name for selected display
            return {
              key: quest._id,
              value: fullName, // Full name for dropdown options
              fullValue: fullName, // Full name for tooltips
              // Truncated name for selected value display
              displayValue:
                fullName.length > maxChars
                  ? `${fullName.substring(0, maxChars)}...`
                  : fullName,
            }
          })

          setQuests({
            isLoading: false,
            quests: questsData,
          })
        }
      } catch (e) {
        setQuests({
          isLoading: false,
          quests: [],
        })
        Error.showError(e)
      }
    }
    getQuestData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Recalculate truncation on window resize
    const handleResize = () => {
      setQuests((prev) => {
        if (prev.quests.length === 0) return prev

        const getMaxChars = () => {
          if (window.innerWidth >= 1200) {
            return 50
          } else if (window.innerWidth >= 992) {
            return 40
          } else if (window.innerWidth >= 768) {
            return 30
          } else {
            return 25
          }
        }
        const maxChars = getMaxChars()
        return {
          ...prev,
          quests: prev.quests.map((quest) => {
            const displayValue =
              quest.fullValue.length > maxChars
                ? `${quest.fullValue.substring(0, maxChars)}...`
                : quest.fullValue
            return {
              ...quest,
              value: quest.fullValue, // Keep full name for dropdown options
              displayValue: displayValue, // Update truncated name for selected display
            }
          }),
        }
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    // Clear quest filter when type changes away from 'quest'
    if (
      globalFilter.type !== CONSTANTS.platformType.QUEST &&
      globalFilter.quest !== ''
    ) {
      setGlobalFilter({
        ...globalFilter,
        quest: '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilter.type])

  return (
    <section className='mb-2-5'>
      <Row className='user-payment-filters'>
        <Col className='col-4 mb-3'>
          <FilterSelection
            {...{
              globalFilter,
              setGlobalFilter,
            }}
            name='promoCode'
            placeholder='Select Promo'
            inputClasses={classNames('user-input', {
              'pointer-cursor': !promoCodes?.isLoading || !isLoading,
              'cursor-disabled': promoCodes?.isLoading || isLoading,
            })}
            options={promoCodes?.promoCodes}
            size='md'
            disabled={promoCodes?.isLoading || isLoading}
            key={globalFilter.promoCode}
          />
        </Col>
        <Col className='col-4 mb-3'>
          {/* Date range of payment */}
          <DateRangePicker
            initialSettings={{
              maxSpan: { months: 6 },
              autoUpdateInput: false,
              opens: 'center',
              startDate: getCurrentDate(),
              endDate: getCurrentDate(),
            }}
            onApply={(start, end) => {
              setGlobalFilter({
                ...globalFilter,
                preDate: start,
                postDate: end,
              })
            }}
            //setOnHide={true}
            className='w-100'
            ref={picker}
            isUseKeyProp={isResetForm}
            {...(isResetForm && {
              key: globalFilter?.preDate || globalFilter?.postDate,
            })}
            disabled={isLoading}
          />
        </Col>
        <Col className='col-4 mb-3'>
          <FilterSelection
            {...{
              globalFilter,
              setGlobalFilter,
            }}
            name='organization'
            placeholder='Select Organization'
            inputClasses={classNames('user-input', {
              'pointer-cursor':
                !companies?.isLoading || !globalFilter.network || !isLoading,
              'cursor-disabled':
                companies?.isLoading || globalFilter.network || isLoading,
            })}
            options={companies?.companies}
            size='md'
            disabled={companies?.isLoading || globalFilter.network || isLoading}
            key={globalFilter.organization}
          />
        </Col>
        <Col className='col-4 mb-3'>
          <FilterSelection
            {...{
              globalFilter,
              setGlobalFilter,
            }}
            name='network'
            placeholder='Select Network'
            inputClasses={classNames('user-input', {
              'pointer-cursor':
                !ecoSystems?.isLoading ||
                !globalFilter.organization ||
                !isLoading,
              'cursor-disabled':
                ecoSystems?.isLoading || globalFilter.organization || isLoading,
            })}
            options={ecoSystems?.ecoSystems}
            size='md'
            disabled={
              ecoSystems?.isLoading || globalFilter.organization || isLoading
            }
            key={globalFilter.network}
          />
        </Col>
        <Col className='col-4 mb-3 mt-2'>
          {/* Payment range - slider */}
          <MultiRangeSlider
            min={CONSTANTS.minAmountOfPaymentRange}
            max={CONSTANTS.maxAmonuntOfPaymentRange}
            defaultValue={CONSTANTS.defaultMinAmountOfPaymentRange}
            onChange={(amount) => {
              setGlobalFilter({
                ...globalFilter,
                preAmount: amount.min,
                postAmount: amount.max,
              })
            }}
            {...(isResetForm && {
              key: globalFilter?.preAmount || globalFilter?.postAmount,
            })}
            disabled={isLoading}
          />
        </Col>
        <Col className='col-4 mb-3'>
          <FilterSelection
            {...{
              globalFilter,
              setGlobalFilter,
            }}
            name='status'
            placeholder='Select Payment Status'
            inputClasses={classNames('user-input', {
              'pointer-cursor': !isLoading,
              'cursor-disabled': isLoading,
            })}
            options={Object.values(CONSTANTS.paymentStatus).map((status) => ({
              key: status,
              value: status.toUpperCase(),
            }))}
            size='md'
            disabled={isLoading}
            key={globalFilter.status}
          />
        </Col>
        <Col className='col-4 mb-3'>
          <FilterSelection
            {...{
              globalFilter,
              setGlobalFilter,
            }}
            name='type'
            placeholder='Select Type'
            inputClasses={classNames('user-input', {
              'pointer-cursor': !isLoading,
              'cursor-disabled': isLoading,
            })}
            options={Object.values(CONSTANTS.platformType).map((type) => {
              // Show "EXPERIENCE" label for quest type, but keep value as "quest"
              const displayLabel =
                type === CONSTANTS.platformType.QUEST
                  ? 'EXPERIENCE'
                  : type.toUpperCase()
              return {
                key: type,
                value: displayLabel,
              }
            })}
            size='md'
            disabled={isLoading}
            key={globalFilter.type}
          />
        </Col>
        <Col className='col-4 mb-3'></Col>
        <Col className='col-4 mb-3'>
          <div className='w-100 quest-filter-wrapper'>
            <FilterSelection
              {...{
                globalFilter,
                setGlobalFilter,
              }}
              name='quest'
              placeholder='Select Experience'
              inputClasses={classNames('user-input w-100 quest-select', {
                'pointer-cursor':
                  !quests?.isLoading &&
                  globalFilter.type === CONSTANTS.platformType.QUEST &&
                  !isLoading,
                'cursor-disabled':
                  quests?.isLoading ||
                  globalFilter.type !== CONSTANTS.platformType.QUEST ||
                  isLoading,
              })}
              options={quests?.quests}
              size='md'
              disabled={
                quests?.isLoading ||
                globalFilter.type !== CONSTANTS.platformType.QUEST ||
                isLoading
              }
              key={globalFilter.quest}
            />
          </div>
        </Col>
      </Row>
      {(globalFilter.preDate ||
        globalFilter.postDate ||
        globalFilter.organization !== '' ||
        globalFilter.network !== '' ||
        globalFilter.promoCode !== '' ||
        globalFilter.preAmount !== CONSTANTS.defaultMinAmountOfPaymentRange ||
        globalFilter.postAmount !== CONSTANTS.maxAmonuntOfPaymentRange ||
        globalFilter.status !== CONSTANTS.paymentStatus.SUCCEEDED ||
        globalFilter.type !== '' ||
        globalFilter.quest !== '') && (
        <ClearFilters
          wrapperClasses='payment-clear-filter text-right'
          onClick={() => {
            handleClearFilter()
            // for reset payment date range
            picker.current.setStartDate('')
            picker.current.setEndDate('')
            setIsResetForm(true)
            setTimeout(() => {
              setIsResetForm(false)
            }, 200)
          }}
          disabled={isLoading}
        />
      )}
    </section>
  )
}

export default PaymentFilters
